import { Link } from 'react-router-dom';

import { SiteNav } from "../components/SiteNav";
import SiteFooter from '../components/SiteFooter';

const H2 = ({ children }) => (
  <h2 className="font-heading text-xl font-medium mt-10 mb-4" style={{ color: '#2A1E5C' }}>{children}</h2>
);
const P = ({ children }) => (
  <p className="font-body text-base leading-relaxed mb-4" style={{ color: '#5D5A6B' }}>{children}</p>
);

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteNav />

      {/* Content */}
      <section className="pt-12 pb-16 sm:pt-16 lg:pt-20 lg:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto" data-testid="terms-content">
          <h1 className="font-heading text-3xl sm:text-4xl font-medium mb-4" style={{ color: '#2A1E5C' }}>
            Payday Relay Beta Terms of Service
          </h1>
          <p className="font-body text-sm mb-8" style={{ color: '#8B7E6A' }} data-testid="terms-last-updated">
            Last updated: September 3, 2026
          </p>

          <div className="prose prose-lg" style={{ color: '#5D5A6B' }}>
            <P>
              These Beta Terms of Service govern your access to Payday Relay, the Payday Relay app, the Relay Calculator,
              related worksheets, Google Sheets-based tools, coaching materials, emails, and beta features
              provided by Earthwise Digital LLC.
            </P>
            <P><strong style={{ color: '#2A1E5C' }}>By joining the beta, you agree to these terms.</strong></P>

            <H2>1. Beta Access</H2>
            <P>
              Payday Relay is currently in beta. That means features may change, break, improve, disappear,
              or work differently while we test and refine the system.
            </P>
            <P>
              You understand that beta access is provided for testing, feedback, and early use. We may update
              the product, pricing, features, workflows, or availability during the beta period.
            </P>

            <H2>2. Educational Tool Only</H2>
            <P>
              Payday Relay is an educational planning tool. It is not financial, tax, legal, accounting,
              investment, or business advice.
            </P>
            <P>
              You are responsible for your own financial decisions, income targets, taxes, savings, debt
              payments, contractor payments, and business operations. For specific advice, talk to a qualified
              CPA, attorney, financial advisor, or other professional.
            </P>

            <H2>3. Your Account, Google Sheets, and Sync</H2>
            <P>
              You agree to provide accurate information when signing up, including your email address and any
              information needed to set up your Payday Relay tools.
            </P>
            <P>
              If you connect Google, you authorize Payday Relay to access only your account email and the single
              Google Sheet it creates for you, as described in our{' '}
              <Link to="/privacy" style={{ color: '#2A1E5C', textDecoration: 'underline' }}>Privacy Policy</Link>.
              The spreadsheet is created in your own Google account and remains yours.
            </P>
            <P>
              <strong style={{ color: '#2A1E5C' }}>Sync limitations.</strong> Sheet sync depends on Google&rsquo;s
              services and on your authorization staying valid. Google may expire or revoke access (for example if
              you change your password, remove the app, or after long inactivity). When that happens the app will
              ask you to reconnect; entries you log in the meantime are kept in the app and written to the sheet
              once you reconnect. Sync can also lag or fail during Google outages. You are responsible for
              reconnecting when asked, and for not renaming or deleting the sheet&rsquo;s tabs or formula cells, if you do, the app may be unable to read or write to it until it is repaired or rebuilt.
            </P>
            <P>You are responsible for keeping access to your email, Google account, and connected accounts secure.</P>

            <H2>4. Intellectual Property</H2>
            <P>
              Payday Relay, Payday Relay Method, Relay Calculator, relay target, payday rhythm,
              related frameworks, worksheets, copy, designs, workflows, formulas, coaching materials, and
              product structure are owned by Earthwise Digital LLC or its licensors.
            </P>
            <P>
              You receive a limited, personal, non-transferable license to use the beta for your own individual
              planning purposes.
            </P>
            <P>
              You may not copy, resell, redistribute, publish, train another product on, reverse engineer,
              recreate, clone, scrape, or use Payday Relay or the Payday Relay Method for competitive,
              commercial, or derivative purposes.
            </P>

            <H2>5. Confidential Beta Materials</H2>
            <P>
              Because this is a beta, you may see early product ideas, language, workflows, designs, formulas,
              or features that are not yet public.
            </P>
            <P>
              You agree not to share, copy, publish, or distribute beta-only materials without written
              permission from Earthwise Digital LLC.
            </P>

            <H2>6. Feedback</H2>
            <P>
              If you share feedback, suggestions, bug reports, testimonials, feature ideas, or product
              comments, you give Earthwise Digital LLC permission to use that feedback to improve, market, and
              develop Payday Relay without owing compensation.
            </P>
            <P>
              We will not publish your full name, personal financial information, or identifiable story as a
              testimonial without your permission.
            </P>

            <H2>7. Payments, Subscriptions, and Cancellation</H2>
            <P>
              The <strong>Free 4-Relay Sprint</strong> is a 28-day trial of the Season Pass. For the standard
              Sprint you add a payment method to start, you are not charged during the 28-day Sprint, and at
              day 28 it converts automatically to a paid <strong>Season Pass</strong> (billed monthly or
              annually) and renews each billing period until you cancel. If you cancel before day 28, you are
              not charged.
            </P>
            <P>
              <strong>Founding beta members</strong> start with no payment method and are not charged to
              join. When the 28-day Sprint ends it simply stops — nothing is billed automatically. If a beta
              member chooses to continue to a paid Season Pass, a founding-member discount of <strong>50% off
              applies for the life of the subscription</strong> (for example $9.50/mo instead of $19/mo) for
              as long as they remain a member. We email you before your Sprint ends.
            </P>
            <P>
              You can cancel at any time from the app or the billing portal. One step, no retention maze.
              If you cancel a monthly Season Pass, your access continues through the rest of the period
              you already paid for, then billing simply stops, you are not charged again.
            </P>
            <P>
              <strong>Payments are non-refundable.</strong> Because the Free Sprint gives you 28 days to try
              the full product at $0 before you are ever charged, we do not offer refunds once a payment is
              made, whether your Season Pass is billed monthly or annually. This applies to annual payments
              in full, an annual Season Pass is not prorated or refunded for the unused months if you cancel
              partway through the year. Questions about a specific charge? Email support@paydayrelay.com.
            </P>
            <P>
              Your Google Sheet stays in your Drive after you cancel. The app simply stops filling it in for you.
            </P>
            <P>
              Payments are processed by Stripe. We do not store your full card number. Prices may change; we will
              give you notice before any change affects your renewal. Promotional codes are subject to the terms
              shown when they are offered.
            </P>

            <H2>8. Acceptable Use</H2>
            <P>
              You agree not to misuse the product, interfere with the service, attempt unauthorized access,
              upload malicious code, use the product for illegal activity, or use beta access to build or
              improve a competing product.
            </P>

            <H2>9. Third-Party Services</H2>
            <P>
              Payday Relay depends on third-party services including Google (Sheets and OAuth), Stripe, Resend,
              AI model providers, browser push services, and hosting, database, and application-platform
              providers.
            </P>
            <P>We are not responsible for outages, policy changes, or failures caused by third-party services.</P>

            <H2>9a. The Caddy and AI-Generated Content</H2>
            <P>
              The Caddy is an AI assistant. Its coaching messages and answers are generated automatically and may
              be incomplete, out of date, or simply wrong. Nothing the Caddy says is financial, tax, legal,
              accounting, or investment advice, and it does not know your full situation. Treat it as a
              supportive coach, verify anything that matters, and consult a qualified professional for decisions
              with real consequences.
            </P>

            <H2>9b. Your Numbers Are Yours to Check</H2>
            <P>
              Payday Relay calculates targets, allocations, tax set-asides, and progress from the numbers you
              enter and from formulas in your sheet. You are responsible for reviewing those inputs and results,
              confirming they fit your circumstances, and correcting anything that looks off. A weekly target,
              a tax percentage, or a PTO balance shown in the app is a planning estimate, not a filing, a
              guarantee, or a substitute for your own records.
            </P>

            <H2>10. No Guarantee of Results</H2>
            <P>
              Payday Relay can help you plan, track, and structure unstructured income. It does not guarantee
              income, profit, tax outcomes, debt payoff, savings results, business success, or financial
              stability.
            </P>
            <P>
              Your results depend on your work, choices, circumstances, clients, rates, expenses, and other
              factors outside our control.
            </P>

            <H2>11. Termination</H2>
            <P>
              We may suspend or end your beta access if you violate these terms, misuse the product, create
              security risks, or use the product for competitive or unauthorized purposes.
            </P>
            <P>You may stop using the beta at any time.</P>

            <H2>12. Disclaimers and Limitation of Liability</H2>
            <P>
              The beta is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; We make no promise that
              it will be uninterrupted, error-free, or suitable for every situation.
            </P>
            <P>
              To the fullest extent allowed by law, Earthwise Digital LLC is not liable for indirect,
              incidental, special, consequential, or lost-profit damages arising from your use of Payday Relay.
            </P>

            <H2>13. Contact</H2>
            <P>
              Questions about these terms can be sent to:{' '}
              <a href="mailto:support@paydayrelay.com" style={{ color: '#2A1E5C', textDecoration: 'underline' }} data-testid="terms-contact-email">
                support@paydayrelay.com
              </a>
            </P>

            <div className="rounded-xl p-5 mt-10" style={{ background: '#FFF3C6', border: '1.5px solid #F5CB5C' }} data-testid="terms-beta-agreement">
              <p className="font-body text-sm font-bold mb-2" style={{ color: '#2A1E5C' }}>Beta Agreement</p>
              <p className="font-body text-sm leading-relaxed" style={{ color: '#5D5A6B' }}>
                When you join the beta, you check a box that says: &ldquo;I have read and agree to the Payday
                Relay Beta Terms of Service and Privacy Policy. I understand that Payday Relay is an
                educational beta tool, not financial, tax, legal, or investment advice. I also agree not to
                copy, reverse engineer, redistribute, or use Payday Relay or the Payday Relay Method for
                competitive purposes.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
